import type { Rgb } from './colorScale';

export type OutlineMaskArgs = {
  /** View-axis depth per pixel, larger nearer, `-Infinity` where nothing was drawn. */
  depth: Float32Array;
  width: number;
  height: number;
  /** Half the extent of the camera's cube along the view axis, which `depth` is measured against. */
  depthExtent: number;
};

/**
 * How far a pixel's depth must stand above its neighbors' average, as a fraction of the depth
 * extent, before it starts and finishes reading as an occluding edge. On a smooth surface the
 * second difference of depth is a curvature term that shrinks with every pixel of resolution; at a
 * ridge passing in front of the ground behind it, it is the size of the gap, at any resolution.
 */
export const OUTLINE_CONTOUR_START = 0.004;
export const OUTLINE_CONTOUR_FULL = 0.012;

/** How strongly outlines are inked where the mask is full. */
export const OUTLINE_OPACITY = 0.85;

/** The ink an outline is drawn in, 0-255 per channel, and how strongly. */
export type OutlineInk = {
  color: Rgb;
  opacity: number;
};

const smoothstep = (edge0: number, edge1: number, value: number): number => {
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

/**
 * How much outline falls on one pixel, 0 to 1.
 *
 * Two kinds of line, both found in the depth buffer alone: the silhouette, on the drawn side of
 * every pixel whose neighbor is empty; and the occluding contour, where a pixel is nearer than the
 * average of its four neighbors by more than smooth curvature explains. Only the nearer side of a
 * depth jump is marked, so each edge is one pixel wide and sits on the ridge doing the hiding, as a
 * pen line would.
 *
 * Along an axis where a neighbor is off the edge of the buffer the second difference is taken as
 * zero, so a surface running off the canvas is not outlined along the canvas edge.
 */
const toOutlineCoverage = (
  depth: Float32Array,
  width: number,
  height: number,
  scale: number,
  x: number,
  y: number
): number => {
  const index = y * width + x;
  const center = depth[index];
  if (center === -Infinity) return 0;

  const left = x > 0 ? depth[index - 1] : null;
  const right = x < width - 1 ? depth[index + 1] : null;
  const up = y > 0 ? depth[index - width] : null;
  const down = y < height - 1 ? depth[index + width] : null;

  if (
    left === -Infinity ||
    right === -Infinity ||
    up === -Infinity ||
    down === -Infinity
  ) {
    return 1;
  }

  const across =
    left === null || right === null ? 0 : 2 * center - left - right;
  const along = up === null || down === null ? 0 : 2 * center - up - down;
  return smoothstep(
    OUTLINE_CONTOUR_START,
    OUTLINE_CONTOUR_FULL,
    (across + along) * scale
  );
};

/** Where the surface's outlines fall, as a coverage from 0 to 1 per pixel. */
export const toOutlineMask = ({
  depth,
  width,
  height,
  depthExtent,
}: OutlineMaskArgs): Float32Array => {
  const mask = new Float32Array(width * height);
  const scale = 1 / Math.max(depthExtent, 1e-9);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      mask[y * width + x] = toOutlineCoverage(
        depth,
        width,
        height,
        scale,
        x,
        y
      );
    }
  }
  return mask;
};

/**
 * Inks the outlines found in `depth` straight into `image`, which must be the same size. In place,
 * and without the full-frame mask {@link toOutlineMask} builds, since this runs every frame of an
 * orbit on the software path.
 */
export const applyOutlinesFromDepth = (
  image: ImageData,
  { depth, depthExtent }: { depth: Float32Array; depthExtent: number },
  { color: [red, green, blue], opacity }: OutlineInk
): void => {
  const { width, height, data: pixels } = image;
  const scale = 1 / Math.max(depthExtent, 1e-9);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const amount =
        toOutlineCoverage(depth, width, height, scale, x, y) * opacity;
      if (amount <= 0) continue;
      const offset = (y * width + x) * 4;
      pixels[offset] += (red - pixels[offset]) * amount;
      pixels[offset + 1] += (green - pixels[offset + 1]) * amount;
      pixels[offset + 2] += (blue - pixels[offset + 2]) * amount;
    }
  }
};
